import { NotificationType, SitePermitStatus } from '~/client/graph'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import User from '~/client/src/shared/models/User'
import Workflow from '~/client/src/shared/models/Workflow'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import {
  MARK_NOTIFICATIONS_AS_ARCHIVED,
  MARK_NOTIFICATIONS_AS_READ,
  SAVE_WORKFLOWS,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { isPermitType } from '~/client/src/shared/types/NotificationTypes'
import canApproveUser from '~/client/src/shared/utils/canApproveUser'
import {
  getCommonStepItems,
  IStatusItem,
  resolveStepForStatus,
} from '~/client/src/shared/utils/permitStatusUtils'
import { toast } from '~/client/src/shared/utils/toast'
import { advanceToNextWorkflowStep } from '~/client/src/shared/utils/workflowStepUtils'

export interface BulkActionsDeps {
  eventsStore: EventsStore
  permitTypesStore: WorkflowTypesStore
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  onAfterAction?: () => void
}

// --------------- Selection helpers ---------------

export function isAllSelected(
  filteredNotifications: readonly BaseNotification[],
  selectedNotifications: readonly BaseNotification[],
): boolean {
  const selectedIds = new Set(selectedNotifications.map(n => n.id))
  return (
    filteredNotifications.length > 0 &&
    filteredNotifications.every(n => selectedIds.has(n.id))
  )
}

export function isSomeSelected(
  filteredNotifications: readonly BaseNotification[],
  selectedNotifications: readonly BaseNotification[],
): boolean {
  const selectedIds = new Set(selectedNotifications.map(n => n.id))
  return (
    !isAllSelected(filteredNotifications, selectedNotifications) &&
    filteredNotifications.some(n => selectedIds.has(n.id))
  )
}

// --------------- Derived data ---------------

export function getSelectedPermits(
  selectedNotifications: readonly BaseNotification[],
): Workflow[] {
  const seen = new Set<string>()
  const permits: Workflow[] = []
  for (const n of selectedNotifications) {
    if (
      isPermitType(n.type) &&
      n.associatedPermit &&
      n.associatedPermit.status !== SitePermitStatus.Draft &&
      !seen.has(n.associatedPermit.id)
    ) {
      seen.add(n.associatedPermit.id)
      permits.push(n.associatedPermit)
    }
  }
  return permits
}

export function hasPermitNotificationsSelected(
  selectedNotifications: readonly BaseNotification[],
): boolean {
  return getSelectedPermits(selectedNotifications).length > 0
}

export function canMarkAsDone(
  selectedNotifications: readonly BaseNotification[],
): boolean {
  const permits = getSelectedPermits(selectedNotifications)
  return permits.some(p => p.status !== SitePermitStatus.Done)
}

export function hasUnreadSelected(
  selectedNotifications: readonly BaseNotification[],
): boolean {
  return selectedNotifications.some(n => !n.wasRead)
}

export function hasReadSelected(
  selectedNotifications: readonly BaseNotification[],
): boolean {
  return selectedNotifications.some(n => n.wasRead)
}

export function getIsAdmin(eventsStore: EventsStore): boolean {
  return eventsStore.appState.userActiveProjectSettings?.isAdmin
}

export function getApprovableUsers(
  selectedNotifications: readonly BaseNotification[],
  projectMembersStore: ProjectMembersStore,
  userProjectsStore: UserProjectsStore,
): User[] {
  return selectedNotifications
    .filter(n => n.type === NotificationType.NewUserCreatedViaSignUp)
    .map(n => projectMembersStore.getById(n.entityId))
    .filter(user => user && canApproveUser(user, userProjectsStore))
}

export function getCommonStepItemsForSelected(
  selectedNotifications: readonly BaseNotification[],
  permitTypesStore: WorkflowTypesStore,
): IStatusItem[] {
  const permits = getSelectedPermits(selectedNotifications)
  if (!permitTypesStore || !permits.length) return []
  return getCommonStepItems(permits, permitTypesStore)
}

// --------------- Actions ---------------

export function markAsRead(
  deps: BulkActionsDeps,
  notifications: readonly BaseNotification[],
): void {
  const count = notifications.length
  deps.eventsStore.dispatch(MARK_NOTIFICATIONS_AS_READ, notifications)
  toast.info(Localization.translator.xMarkedAsRead(count))
  deps.onAfterAction?.()
}

export function markAsUnread(
  deps: BulkActionsDeps,
  notifications: readonly BaseNotification[],
): void {
  const count = notifications.length
  deps.eventsStore.dispatch(
    MARK_NOTIFICATIONS_AS_READ,
    notifications,
    undefined,
    false,
  )
  toast.info(Localization.translator.xMarkedAsUnread(count))
  deps.onAfterAction?.()
}

export function archive(
  deps: BulkActionsDeps,
  notifications: readonly BaseNotification[],
): void {
  const count = notifications.length
  deps.eventsStore.dispatch(MARK_NOTIFICATIONS_AS_ARCHIVED, notifications)
  toast.info(Localization.translator.xArchived(count))
  deps.onAfterAction?.()
}

export function unarchive(
  deps: BulkActionsDeps,
  notifications: readonly BaseNotification[],
): void {
  const count = notifications.length
  deps.eventsStore.dispatch(
    MARK_NOTIFICATIONS_AS_ARCHIVED,
    notifications,
    undefined,
    false,
  )
  toast.info(Localization.translator.xUnarchived(count))
  deps.onAfterAction?.()
}

export async function bulkApprove(
  deps: BulkActionsDeps,
  selectedNotifications: readonly BaseNotification[],
): Promise<void> {
  const users = getApprovableUsers(
    selectedNotifications,
    deps.projectMembersStore,
    deps.userProjectsStore,
  )
  const count = users.length
  if (!count) return

  await deps.projectMembersStore.approveProjectMembers(users)
  toast.info(`${count} ${Localization.translator.approved}`)
  deps.onAfterAction?.()
}

export function confirmStatusUpdate(
  deps: BulkActionsDeps,
  pendingItem: IStatusItem,
  pendingPermits: Workflow[],
): void {
  if (!pendingItem) return

  const toSave: Workflow[] = pendingPermits.map(permit => {
    const copy = permit.copy()
    copy.setStatus(pendingItem.status)

    const permitType = deps.permitTypesStore?.getPermitTypeById(permit.typeId)
    if (permitType) {
      const step = resolveStepForStatus(
        pendingItem.status,
        pendingItem.workflowStepLevel,
        permitType,
      )
      if (step) {
        copy.setWorkflowStepId(step.id)
        if (
          pendingItem.status !== SitePermitStatus.Denied &&
          pendingItem.status !== SitePermitStatus.Failed
        ) {
          advanceToNextWorkflowStep(copy, permitType.stepsWithoutRecurring)
        } else {
          const steps = permitType.stepsWithoutRecurring
          const isLastStep = step.id === steps[steps.length - 1]?.id
          copy.setWorkflowStepLevel(
            isLastStep ? 0 : getWorkflowStepLevel(step.id, steps),
          )
        }
      }
    }

    return copy
  })

  if (toSave.length) {
    deps.eventsStore.dispatch(SAVE_WORKFLOWS, toSave)
    toast.info(Localization.translator.statusUpdated)
  }

  deps.onAfterAction?.()
}
